/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import Alert from './Alert';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { injectTestId } from '@cvent/nucleus-test-automation';

/**
 * The containing wrapper elements around individual Alert items.
 */
export default class Alerts extends React.Component {
  static propTypes = {
    /** Alerts. this array should contain all of the necessary information for all of the actual alert elements that are going to be rendered.  */
    alerts: PropTypes.array,
    /** Displays dismiss button on Alert children. */
    dismissible: PropTypes.bool,
    /** Interval for self-dismissal (user-level variant only) */
    dismissInterval: PropTypes.number,
    /** Ref to be applied to outer container, useful for "scrollIntoView" */
    forwardedRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    /** Forces position relative */
    isInline: PropTypes.bool,
    /** Right to left language preference */
    isRtl: PropTypes.bool,
    /** Max-width for Alert children */
    maxWidth: PropTypes.string,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** Alert variant. Note: this is set here so that the positioning is correct: different alerts go in different places, and this is used to corral them.  */
    variant: PropTypes.oneOf(['app', 'page', 'user'])
  };

  static defaultProps = {
    alerts: [],
    dismissible: false,
    isInline: false,
    isRtl: false,
    variant: 'page',
    maxWidth: 'unset'
  };

  constructor(props) {
    super(props);
    this.state = {
      currentChild: 0
    };
  }

  render() {
    const {
      alerts,
      isInline,
      isRtl,
      testID,
      maxWidth,
      variant,
      dismissInterval,
      forwardedRef
    } = this.props;

    const alertsCount = alerts.length;

    return (
      <ThemeConsumer>
        {theme => (
          <React.Fragment>
            {this.state.currentChild < alertsCount && (
              <div
                {...injectTestId(testID)}
                role="group"
                ref={forwardedRef}
                css={getAlertsStyles({
                  theme,
                  isInline,
                  isRtl,
                  variant: variant.toLowerCase()
                })}
              >
                {this.renderItems(
                  isRtl,
                  isInline,
                  maxWidth,
                  variant.toLowerCase(),
                  alerts,
                  dismissInterval
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </ThemeConsumer>
    );
  }

  renderItems(isRtl, isInline, maxWidth, variant, alerts, dismissInterval) {
    const shared = {
      isRtl,
      isInline,
      maxWidth,
      variant,
      dismissInterval
    };
    if (!alerts) {
      return null;
    }
    return alerts.map((item, index) => {
      if (index === this.state.currentChild || variant === 'page') {
        return (
          <Alert
            key={index}
            {...shared}
            {...item}
            onIncrementChild={() => {
              this.incrementCurrentChild();
            }}
            variant={variant}
          />
        );
      }
    });
  }

  incrementCurrentChild = () => {
    this.setState({
      currentChild: this.state.currentChild + 1
    });
  };
}

function getAlertsStyles({ theme, isInline, isRtl, variant }) {
  const positionRelative = variant !== 'user' || isInline;
  return {
    margin: 0,
    lineHeight: 'auto',
    marginTop: 0,
    marginBottom: 0,
    boxSizing: 'initial',
    alignItems: 'center',
    display: 'flex',
    direction: isRtl ? 'rtl' : 'ltr',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    left: positionRelative ? 'auto' : 0,
    padding: positionRelative ? 0 : '1rem',
    position: positionRelative ? 'relative' : 'fixed',
    right: positionRelative ? 'auto' : 0,
    top: positionRelative ? 'auto' : 0,
    zIndex: positionRelative ? 'auto' : theme.depth.zIndex[800]
  };
}
